import { db } from "../../services/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  onSnapshot,
  setDoc,
  arrayUnion,
  getDocs,
  limit
} from "firebase/firestore";
import { computeServiceFilters } from "../../utils/rollupServiceFilters";

function normalizeServiceCategory(category) {
  return String(category || "").trim().toLowerCase();
}

function getServiceItemCollectionCandidates(category) {
  switch (normalizeServiceCategory(category)) {
    case "food":
      return ["menu", "servicesAndPrices"];
    case "hair":
    case "other":
      return ["services", "servicesAndPrices"];
    case "local":
      return [];
    default:
      return ["servicesAndPrices"];
  }
}

async function resolveServiceItemDoc({ serviceId, itemId, category, collectionName }) {
  let resolvedCategory = category;

  if (!collectionName && !resolvedCategory && serviceId) {
    const serviceSnap = await getDoc(doc(db, "services", serviceId));
    if (serviceSnap.exists()) {
      resolvedCategory = serviceSnap.data()?.category;
    }
  }

  const candidates = collectionName
    ? [collectionName]
    : getServiceItemCollectionCandidates(resolvedCategory);

  for (const candidate of candidates) {
    const itemRef = doc(db, "services", serviceId, candidate, itemId);
    const itemSnap = await getDoc(itemRef);
    if (itemSnap.exists()) {
      return { itemRef, itemSnap, collectionName: candidate };
    }
  }

  return null;
}

export async function getProviderPublicInfoClient(providerId) {
  if (!providerId) {
    throw new Error("providerId is required");
  }

  const providerRef = doc(db, "providers", providerId);
  const snap = await getDoc(providerRef);

  if (!snap.exists()) {
    throw new Error("Provider not found");
  }

  const data = snap.data();

  return data;

  // IMPORTANT: DO this later only return public-facing info here
//   return {
//     id: snap.id,
//     name: data.name,
//     publicEmail: data.publicEmail || data.email || null, // or drop email entirely if you want
//     serviceIds: data.serviceIds || [],
//     // DO NOT expose: plan, internal flags, etc.
//   };
}


export async function disableServiceItemClient({serviceId, serviceItemId}){
  const resolved = await resolveServiceItemDoc({ serviceId, itemId: serviceItemId });
  if (!resolved) {
    throw new Error("Item not found");
  }
  await updateDoc(resolved.itemRef, { temporarilyDisabled: true });
}

export async function enableServiceItemClient({serviceId, serviceItemId}){
  const resolved = await resolveServiceItemDoc({ serviceId, itemId: serviceItemId });
  if (!resolved) {
    throw new Error("Item not found");
  }
  await updateDoc(resolved.itemRef, { temporarilyDisabled: false });
}

export async function disableServiceClient({ serviceId }) {
  if (!serviceId) {
    throw new Error("serviceId is required");
  }

  await updateDoc(doc(db, "services", serviceId), { disabled: true });
}

export async function enableServiceClient({ serviceId }) {
  if (!serviceId) {
    throw new Error("serviceId is required");
  }

  await updateDoc(doc(db, "services", serviceId), { disabled: false });
}

export async function sendEditRequestClient({requestData}){
  const docRef = await addDoc(collection(db, 'editRequests'), requestData);
  return docRef;
}

export function subscribeToEditRequestsClient({providerId, onUpdate}) {
  const q = query(
    collection(db, 'editRequests'),
    where('providerId', '==', providerId)
  );

  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
      urgentDeadline: doc.data().urgentDeadline?.toDate()
    })).sort((a, b) => (b.createdAt || new Date(0)) - (a.createdAt || new Date(0)));
    
    onUpdate(requests);
  });
}

export async function cancelEditRequestClient({requestId, request}) {
  await updateDoc(doc(db, 'editRequests', requestId), {
    status: 'cancelled',
    updatedAt: new Date()
  });
  
  if (request?.temporarilyDisabled && request?.temporarilyDisabledScope === 'service' && request?.serviceId) {
    await updateDoc(doc(db, 'services', request.serviceId), { disabled: false });
    return;
  }

  if (request?.temporarilyDisabled && request?.serviceItemId) {
    const resolved = await resolveServiceItemDoc({
      serviceId: request.serviceId,
      itemId: request.serviceItemId,
      category: request.category,
      collectionName: request.collectionName
    });

    if (resolved) {
      await updateDoc(resolved.itemRef, { temporarilyDisabled: false });
    }
  }
}

export async function updateServiceOpenStatusClient({serviceId, isOpen}) {
  const serviceRef = doc(db, 'services', serviceId);
  await updateDoc(serviceRef, { isCurrentlyOpen: isOpen });
}

export async function getServiceItemClient({serviceId, itemId, category, collectionName}) {
  const resolved = await resolveServiceItemDoc({ serviceId, itemId, category, collectionName });
  if (!resolved) {
    throw new Error('Item not found');
  }
  return { id: resolved.itemSnap.id, ...resolved.itemSnap.data(), collectionName: resolved.collectionName };
}

export async function updateContactMethodsClient({providerId, contactMethods}) {
  const providerRef = doc(db, 'providers', providerId);
  await updateDoc(providerRef, { contactMethods });
}

export async function getServiceOrderPlatformsClient({serviceId}) {
  const serviceDoc = await getDoc(doc(db, 'services', serviceId));
  if (!serviceDoc.exists()) {
    throw new Error('Service not found');
  }
  return serviceDoc.data().orderPlatforms || [];
}

export async function updateAvailabilityClient({providerId, availability}) {
  const providerRef = doc(db, 'providers', providerId);
  await updateDoc(providerRef, { availability });
}

export async function checkProviderExistsClient({ providerId }) {
  const providerDoc = await getDoc(doc(db, 'providers', providerId));
  return providerDoc.exists();
}

export async function createProviderClient({ providerId, name, email, contactMethods }) {
  const providerRef = doc(db, 'providers', providerId);
  await setDoc(providerRef, {
    name,
    email,
    plan: 'none',
    approved: false,
    contactMethods
  }, { merge: true });
  
  const userRef = doc(db, 'users', providerId);
  await updateDoc(userRef, { isProvider: true });
}

export async function createServiceClient(serviceData) {
  const serviceRef = doc(collection(db, 'services'));
  const serviceId = serviceRef.id;
  const serviceCities = Array.isArray(serviceData.locations)
    ? [...new Set(serviceData.locations.map((location) => String(location?.city || '').trim().toLowerCase()).filter(Boolean))]
    : [];
  
  const fullServiceData = {
    ...serviceData,
    serviceCities,
    approved: false,
    disabled: false,
    type: 'vendor',
    country: 'canada',
    clickCount: 0,
    createdAt: new Date()
  };
  
  await setDoc(serviceRef, fullServiceData);
  
  // Add to provider's service list
  const providerRef = doc(db, 'providers', serviceData.providerId);
  await updateDoc(providerRef, {
    serviceIds: arrayUnion(serviceId)
  });
  
  // Create service request
  await setDoc(doc(db, 'service_requests', serviceId), {
    ...fullServiceData,
    serviceId,
    requestedAt: new Date()
  });
  
  return { serviceId };
}

export async function getProviderAvailabilityClient({ providerId }) {
  const providerDoc = await getDoc(doc(db, 'providers', providerId));
  if (providerDoc.exists()) {
    return providerDoc.data().availability || {};
  }
  return {};
}

export async function updateServiceBookingPrefsClient({serviceId, bookingPrefs}) {
  const serviceRef = doc(db, 'services', serviceId);
  await updateDoc(serviceRef, { bookingPrefs });
}

export async function updateServiceItemClient({serviceId, itemId, itemData, category, collectionName}) {
  const resolved = await resolveServiceItemDoc({ serviceId, itemId, category, collectionName });
  if (!resolved) {
    throw new Error("Item not found");
  }
  await updateDoc(resolved.itemRef, itemData);
}

export async function getServiceItemsPreviewClient({serviceId, previewLimit = 4}) {
  const itemsQuery = query(
    collection(db, 'services', serviceId, 'servicesAndPrices'),
    limit(previewLimit)
  );
  const itemsSnapshot = await getDocs(itemsQuery);
  const items = itemsSnapshot.docs
    .filter(doc => !doc.data().deleted)
    .map(doc => ({ id: doc.id, ...doc.data() }));

  return {
    items,
    hasMoreItems: itemsSnapshot.size >= previewLimit,
    readCount: itemsSnapshot.size
  };
}

export async function getServiceItemsClient({ serviceId, category }) {
  if (!serviceId) {
    throw new Error("serviceId is required");
  }

  const collectionCandidates = getServiceItemCollectionCandidates(category);

  if (collectionCandidates.length === 0) {
    return {
      items: [],
      collectionName: null,
      readCount: 0
    };
  }

  for (let index = 0; index < collectionCandidates.length; index += 1) {
    const collectionName = collectionCandidates[index];
    const itemsSnapshot = await getDocs(collection(db, "services", serviceId, collectionName));
    const items = itemsSnapshot.docs
      .filter(itemDoc => !itemDoc.data()?.deleted)
      .map(itemDoc => ({
        id: itemDoc.id,
        ...itemDoc.data(),
        collectionName
      }));

    if (items.length > 0 || index === collectionCandidates.length - 1) {
      return {
        items,
        collectionName,
        readCount: itemsSnapshot.size
      };
    }
  }

  return {
    items: [],
    collectionName: collectionCandidates[0] || null,
    readCount: 0
  };
}

export async function updateServiceOrderPlatformsClient({serviceId, orderPlatforms, bookingMethod}) {
  const serviceRef = doc(db, 'services', serviceId);
  const updatePayload = { orderPlatforms };

  if (bookingMethod !== undefined) {
    updatePayload.bookingMethod = bookingMethod;
  }

  await updateDoc(serviceRef, updatePayload);
}

export async function updateServiceFilters(serviceId) {
  const serviceDoc = await getDoc(doc(db, "services", serviceId));
  const category = serviceDoc.exists() ? serviceDoc.data()?.category : null;
  const collectionCandidates = getServiceItemCollectionCandidates(category);
  let items = [];

  for (let index = 0; index < collectionCandidates.length; index += 1) {
    const collectionName = collectionCandidates[index];
    const itemsSnapshot = await getDocs(collection(db, "services", serviceId, collectionName));
    const nextItems = itemsSnapshot.docs
      .filter(itemDoc => !itemDoc.data()?.deleted)
      .map(itemDoc => itemDoc.data());

    if (nextItems.length > 0 || index === collectionCandidates.length - 1) {
      items = nextItems;
      break;
    }
  }

  const filters = computeServiceFilters(items);
  await updateDoc(doc(db, 'services', serviceId), { filters });
}
